import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { IconEditorial, IconResearch, IconHumanAI, IconUpdates } from './icons/BlinkIcons';
export function WhyBlinkblis() {
  const {
    t
  } = useLanguage();
  const reducedMotion = useReducedMotion();
  const [isCompact, setIsCompact] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px), (hover: none), (pointer: coarse)');
    const update = () => setIsCompact(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const motionEnabled = !reducedMotion && !isCompact;
  return <section className="py-20 md:py-32 relative z-10 border-t border-white/5 bg-surface/20" data-id="element-285">
      <div className="max-w-7xl mx-auto px-5 sm:px-6" data-id="element-286">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-287">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-288">
            <h2 className="text-[42px] md:text-5xl lg:text-6xl font-display font-medium mb-6 md:mb-8 leading-[1.1]" data-id="element-289">
              {t('trust.title')}
            </h2>
            <p className="text-white/60 text-[18px] md:text-xl leading-relaxed font-light mb-10 md:mb-12 max-w-lg" data-id="element-290">
              {t('trust.subtitle')}
            </p>

            <div className="space-y-8" data-id="element-291">
              {[{
              icon: IconEditorial,
              title: t('trust.pillars.editorial.title'),
              desc: t('trust.pillars.editorial.desc')
            }, {
              icon: IconResearch,
              title: t('trust.pillars.research.title'),
              desc: t('trust.pillars.research.desc')
            }, {
              icon: IconHumanAI,
              title: t('trust.pillars.humanai.title'),
              desc: t('trust.pillars.humanai.desc')
            }, {
              icon: IconUpdates,
              title: t('trust.pillars.updates.title'),
              desc: t('trust.pillars.updates.desc')
            }].map((item, i) => <div key={i} className="flex gap-4 md:gap-6 items-start group" data-id="element-292">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-white/5 flex items-center justify-center shrink-0 mt-1 md:group-hover:bg-accent/10 transition-colors duration-500 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-293">
                    <item.icon className="w-5 h-5 md:w-6 md:h-6 text-white/60 md:group-hover:text-accent transition-colors duration-500" data-id="element-294" />
                  </div>
                  <div data-id="element-295">
                    <h4 className="text-[18px] md:text-xl font-display font-medium mb-1 md:mb-2 text-white/90 md:group-hover:text-white transition-colors" data-id="element-296">
                      {item.title}
                    </h4>
                    <p className="text-white/50 text-[15px] md:text-base font-light leading-relaxed" data-id="element-297">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative h-[380px] md:h-[500px] max-w-[360px] mx-auto md:max-w-none w-full rounded-[2.5rem] glass-panel border border-white/10 overflow-hidden flex items-center justify-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-id="element-298">
            <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] rounded-[2.5rem] pointer-events-none" data-id="element-299" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,106,0,0.15),transparent_50%)]" data-id="element-300" />

            {/* Abstract UI representation */}
            <motion.div className="w-[85%] h-[85%] border border-white/10 rounded-2xl bg-surface/40 backdrop-blur-2xl p-4 md:p-6 flex flex-col gap-4 md:gap-5 shadow-[0_24px_64px_rgba(0,0,0,0.6)] relative overflow-hidden" animate={!motionEnabled ? undefined : {
            y: [-4, 4, -4]
          }} transition={!motionEnabled ? undefined : {
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-301">
              <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] rounded-2xl pointer-events-none" data-id="element-302" />

              {/* Header */}
              <div className="flex items-center justify-between" data-id="element-303">
                <div className="w-1/4 h-3 rounded-full bg-white/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-304" />
                <div className="flex gap-2" data-id="element-305">
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-306" />
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-307" />
                  <div className="w-3 h-3 rounded-full bg-white/10" data-id="element-308" />
                </div>
              </div>

              <div className="w-full h-px bg-white/5" data-id="element-309" />

              {/* Content */}
              <div className="flex-1 flex gap-5" data-id="element-310">
                {/* Sidebar */}
                <div className="w-1/3 h-full flex flex-col gap-3" data-id="element-311">
                  <div className="w-full h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-312" />
                  <div className="w-3/4 h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-313" />
                  <div className="w-5/6 h-8 rounded-lg bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-314" />
                </div>

                {/* Main Area */}
                <div className="flex-1 h-full flex flex-col gap-4" data-id="element-315">
                  <div className="w-full h-32 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/20 shadow-[inset_0_1px_1px_rgba(255,106,0,0.2)] relative overflow-hidden" data-id="element-316">
                    {/* Abstract Chart Line */}
                    <motion.svg className="absolute bottom-0 left-0 w-full h-full text-accent/40" viewBox="0 0 100 100" preserveAspectRatio="none" animate={reducedMotion ? undefined : {
                    y: [-2, 2, -2]
                  }} transition={reducedMotion ? undefined : {
                    duration: 4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 1
                  }} data-id="element-317">
                      <path d="M0,100 L0,80 Q25,70 50,40 T100,20 L100,100 Z" fill="currentColor" opacity="0.2" data-id="element-318" />
                      <path d="M0,80 Q25,70 50,40 T100,20" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-319" />
                    </motion.svg>
                  </div>
                  <div className="flex gap-4 flex-1" data-id="element-320">
                    <div className="flex-1 rounded-xl bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-321" />
                    <div className="flex-1 rounded-xl bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-322" />
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}